import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Box,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Checkbox,
    ListItemText,
    SelectChangeEvent,
    IconButton,
    Typography,
    TextField,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CopyToClipboardButton from '../../../../atomic/atoms/SCUICopyToClipboardButton/SCUICopyToClipboardButton';
import { CRON } from './cron.classes';
import { parseCronExpressionParts } from '../../helper/Cron.helper';

type Frequency = 'Daily' | 'Hourly' | 'Monthly' | 'Minutely' | 'Yearly' | 'Weekly';

interface CronUIProps {
    onChange: (cronExpression: string) => void;
    defaultCron: string;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const hours = Array.from({ length: 24 }, (_, i) => i);
const minutes = Array.from({ length: 60 }, (_, i) => i);
const days = Array.from({ length: 31 }, (_, i) => i + 1);

const CronUI: React.FC<CronUIProps> = ({ onChange, defaultCron }) => {
    const [frequency, setFrequency] = useState<Frequency>('Daily');
    const [selectedMinutes, setSelectedMinutes] = useState<number[]>([]);
    const [iteratedMinutes, setIteratedMinutes] = useState<number | null>(null);
    const [selectedHours, setSelectedHours] = useState<number[]>([]);
    const [selectedDays, setSelectedDays] = useState<number[]>([]);
    const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
    const [selectedWeekDays, setSelectedWeekDays] = useState<string[]>([]);
    const [cronExpression, setCronExpression] = useState<string>('');

    const { t } = useTranslation();

    const months = [
        t('general.january'),
        t('general.february'),
        t('general.march'),
        t('general.april'),
        t('general.may'),
        t('general.june'),
        t('general.july'),
        t('general.august'),
        t('general.september'),
        t('general.october'),
        t('general.november'),
        t('general.december'),
    ];

    const weekDays = [
        t('general.sunday'),
        t('general.monday'),
        t('general.tuesday'),
        t('general.wednesday'),
        t('general.thursday'),
        t('general.friday'),
        t('general.saturday'),
    ];

    const generatedCronExpression = useMemo(() => {
        const getPartValue = (part: any[], formatter?: (item: any) => any) => {
            const values = part?.map((item) => {
                if (formatter) {
                    return formatter(item);
                }
                return item;
            });
            return values?.includes(-1) ? '*' : values?.join(',') || '*';
        };

        const minutePart = getPartValue(selectedMinutes);
        const hourPart = getPartValue(selectedHours);
        const dayPart = frequency === 'Yearly' || frequency === 'Monthly' ? getPartValue(selectedDays) : '*';
        const monthPart =
            frequency === 'Yearly' ? getPartValue(selectedMonths, (month) => months?.indexOf(month) + 1) : '*';
        const weekDayPart =
            frequency === 'Weekly' ? getPartValue(selectedWeekDays, (day) => weekDays?.indexOf(day)) : '*';

        if (frequency === 'Yearly') {
            return `${minutePart} ${hourPart} ${dayPart} ${monthPart} *`;
        }

        if (frequency === 'Weekly') {
            return `${minutePart} ${hourPart} * * ${weekDayPart}`;
        }

        if (frequency === 'Monthly') {
            return `${minutePart} ${hourPart} ${dayPart} * *`;
        }

        if (frequency === 'Daily') {
            return `${minutePart} ${hourPart} * * *`;
        }

        if (frequency === 'Hourly') {
            return `${minutePart} * * * *`;
        }

        if (frequency === 'Minutely') {
            if (iteratedMinutes) {
                return `*/${iteratedMinutes} * * * *`;
            }
            return `* * * * *`;
        }

        return '* * * * *';
    }, [frequency, selectedMinutes, selectedHours, selectedDays, selectedMonths, selectedWeekDays, iteratedMinutes]);

    useEffect(() => {
        setCronExpression(generatedCronExpression);
        onChange(generatedCronExpression);
    }, [generatedCronExpression, onChange]);

    const handleFrequencyChange = (event?: SelectChangeEvent<Frequency>) => {
        if (event) {
            setFrequency(event?.target.value as Frequency);
        } else {
            setFrequency('Daily');
        }
        setSelectedMinutes([]);
        setSelectedHours([]);
        setSelectedDays([]);
        setSelectedMonths([]);
        setSelectedWeekDays([]);
    };

    const handleMinutesChange = (event: SelectChangeEvent<number[]>) => {
        setSelectedMinutes(event.target.value as number[]);
    };

    const handleHoursChange = (event: SelectChangeEvent<number[]>) => {
        setSelectedHours(event.target.value as number[]);
    };

    const handleDaysChange = (event: SelectChangeEvent<number[]>) => {
        setSelectedDays(event.target.value as number[]);
    };

    const handleMonthsChange = (event: SelectChangeEvent<string[]>) => {
        setSelectedMonths(event.target.value as string[]);
    };

    const handleWeekDaysChange = (event: SelectChangeEvent<string[]>) => {
        setSelectedWeekDays(event.target.value as string[]);
    };

    const handleClearCron = () => {
        setCronExpression('');
        handleFrequencyChange();
    };

    const handleCronChange = (tempCron: string) => {
        const cron = tempCron;
        setCronExpression(cron);

        const parsed = parseCronExpressionParts(cron, months, weekDays);
        if (!parsed) {
            return;
        }

        if (parsed.iteratedMinutes !== undefined) {
            setIteratedMinutes(parsed.iteratedMinutes);
        }
        if (parsed.selectedMinutes) {
            setSelectedMinutes(parsed.selectedMinutes);
        }
        if (parsed.selectedHours) {
            setSelectedHours(parsed.selectedHours);
        }
        if (parsed.selectedDays) {
            setSelectedDays(parsed.selectedDays);
        }
        if (parsed.selectedMonths) {
            setSelectedMonths(parsed.selectedMonths);
        }
        if (parsed.selectedWeekDays) {
            setSelectedWeekDays(parsed.selectedWeekDays);
        }
        if (parsed.frequency) {
            setFrequency(parsed.frequency as Frequency);
        }
    };

    useEffect(() => {
        if (defaultCron) {
            handleCronChange(defaultCron);
        }
    }, [defaultCron]);

    return (
        <Box
            className={CRON.root}
            sx={{
                '.MuiFormLabel-root': {
                    fontSize: '1.5rem !important',
                },
                padding: '24px 12px',
            }}
        >
            <FormControl className={CRON.frequencyControl} fullWidth sx={{ mb: 2 }}>
                <InputLabel className={CRON.frequencyLabel} id="frequency">
                    {t('cron.frequency')}
                </InputLabel>
                <Select
                    className={CRON.frequencySelect}
                    labelId="labelApp"
                    label={'Frequency'}
                    value={frequency}
                    onChange={handleFrequencyChange}
                >
                    <MenuItem className={CRON.frequencyOption} value="Minutely">
                        {t('cron.minutely')}
                    </MenuItem>
                    <MenuItem className={CRON.frequencyOption} value="Hourly">
                        {t('cron.hourly')}
                    </MenuItem>
                    <MenuItem className={CRON.frequencyOption} value="Daily">
                        {t('cron.daily')}
                    </MenuItem>
                    <MenuItem className={CRON.frequencyOption} value="Weekly">
                        {t('cron.weekly')}
                    </MenuItem>
                    <MenuItem className={CRON.frequencyOption} value="Monthly">
                        {t('cron.monthly')}
                    </MenuItem>
                    <MenuItem className={CRON.frequencyOption} value="Yearly">
                        {t('cron.yearly')}
                    </MenuItem>
                </Select>
            </FormControl>
            {frequency === 'Minutely' && (
                <FormControl className={CRON.iteratedMinutesControl} fullWidth sx={{ mb: 2 }}>
                    <TextField
                        className={CRON.iteratedMinutesInput}
                        value={iteratedMinutes}
                        label={t('cron.iterated')}
                        onChange={(e: any) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value === '0') {
                                setIteratedMinutes(null);
                                return;
                            }
                            setIteratedMinutes(+value);
                        }}
                        size="small"
                        slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
                        sx={{ width: '100%' }}
                    />
                </FormControl>
            )}
            {frequency === 'Yearly' && (
                <FormControl className={CRON.monthsControl} fullWidth sx={{ mb: 2 }}>
                    <InputLabel className={CRON.monthsLabel} id="months">
                        {t('cron.months')}
                    </InputLabel>
                    <Select
                        className={CRON.monthsSelect}
                        multiple
                        labelId="months"
                        label={'Months'}
                        value={selectedMonths}
                        onChange={handleMonthsChange}
                        renderValue={(selected) => selected.join(', ')}
                        MenuProps={MenuProps}
                    >
                        {months.map((month) => (
                            <MenuItem className={CRON.monthsOption} key={month} value={month} data-month={month}>
                                <Checkbox className={CRON.optionCheckbox} checked={selectedMonths?.includes(month)} />
                                <ListItemText className={CRON.optionText} primary={month} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {['Yearly', 'Monthly'].includes(frequency) && (
                <FormControl className={CRON.daysControl} fullWidth sx={{ mb: 2 }}>
                    <InputLabel className={CRON.daysLabel} id="days">
                        {t('cron.days')}
                    </InputLabel>
                    <Select
                        className={CRON.daysSelect}
                        multiple
                        labelId="days"
                        label={'Days'}
                        value={selectedDays}
                        onChange={handleDaysChange}
                        renderValue={(selected) => selected.join(', ')}
                        MenuProps={MenuProps}
                    >
                        {days.map((day) => (
                            <MenuItem className={CRON.daysOption} key={day} value={day} data-day={day}>
                                <Checkbox className={CRON.optionCheckbox} checked={selectedDays?.includes(day)} />
                                <ListItemText className={CRON.optionText} primary={day} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {frequency === 'Weekly' && (
                <FormControl className={CRON.weekDaysControl} fullWidth sx={{ mb: 2 }}>
                    <InputLabel className={CRON.weekDaysLabel} id="weeks">
                        {t('cron.weeks')}
                    </InputLabel>
                    <Select
                        className={CRON.weekDaysSelect}
                        multiple
                        labelId="weeks"
                        label={'Week Days'}
                        value={selectedWeekDays}
                        onChange={handleWeekDaysChange}
                        renderValue={(selected) => selected.join(', ')}
                        MenuProps={MenuProps}
                    >
                        {weekDays.map((day) => (
                            <MenuItem className={CRON.weekDaysOption} key={day} value={day} data-weekday={day}>
                                <Checkbox className={CRON.optionCheckbox} checked={selectedWeekDays?.includes(day)} />
                                <ListItemText className={CRON.optionText} primary={day} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {['Yearly', 'Monthly', 'Weekly', 'Daily'].includes(frequency) && (
                <FormControl className={CRON.hoursControl} fullWidth sx={{ mb: 2 }}>
                    <InputLabel className={CRON.hoursLabel} id="hours">
                        {t('cron.hours')}
                    </InputLabel>
                    <Select
                        className={CRON.hoursSelect}
                        multiple
                        label={'Hours'}
                        value={selectedHours}
                        onChange={handleHoursChange}
                        renderValue={(selected) =>
                            selected.map((hour) => `${hour.toString().padStart(2, '0')}:00`).join(', ')
                        }
                        MenuProps={MenuProps}
                    >
                        {hours.map((hour) => (
                            <MenuItem className={CRON.hoursOption} key={hour} value={hour} data-hour={hour}>
                                <Checkbox className={CRON.optionCheckbox} checked={selectedHours?.includes(hour)} />
                                <ListItemText
                                    className={CRON.optionText}
                                    primary={`${hour.toString().padStart(2, '0')}:00`}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {['Yearly', 'Monthly', 'Weekly', 'Daily', 'Hourly'].includes(frequency) && (
                <FormControl className={CRON.minutesControl} fullWidth sx={{ mb: 2 }}>
                    <InputLabel className={CRON.minutesLabel} id="minutes">
                        {t('cron.minutes')}
                    </InputLabel>
                    <Select
                        className={CRON.minutesSelect}
                        multiple
                        labelId="minutes"
                        label={'Minutes'}
                        value={selectedMinutes}
                        onChange={handleMinutesChange}
                        renderValue={(selected) => selected.join(', ')}
                        MenuProps={MenuProps}
                    >
                        {minutes.map((minute) => (
                            <MenuItem className={CRON.minutesOption} key={minute} value={minute} data-minute={minute}>
                                <Checkbox className={CRON.optionCheckbox} checked={selectedMinutes?.includes(minute)} />
                                <ListItemText className={CRON.optionText} primary={minute} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            <Box
                className={CRON.previewRow}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f7f7f7',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '0px 0px 0px 16px',
                    mb: 2,
                    height: '36px',
                }}
            >
                <Typography className={CRON.previewText} sx={{ height: '12px' }}>
                    {cronExpression || '* * * * *'}
                </Typography>
                <Box className={CRON.previewActions} sx={{ marginLeft: 'auto' }}>
                    <IconButton
                        className={CRON.clearButton}
                        sx={{ marginLeft: '16px', borderRadius: '0px', height: '32px', width: '32px' }}
                        onClick={handleClearCron}
                    >
                        <ClearIcon className={CRON.clearIcon} />
                    </IconButton>
                    <CopyToClipboardButton
                        className={CRON.copyButton}
                        textToCopy={cronExpression}
                        sx={{ height: '32px', width: '32px', marginLeft: '4px', borderRadius: '0px', padding: '0px' }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default CronUI;
